import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, Calendar, Clock, BookOpen, MapPin, User, Copy, Sparkles } from 'lucide-react';
import { format } from 'date-fns';

const EventModal = ({ event, onSave, onDelete, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    description: '',
    lesson_plan: '',
    subject: '',
    room: '',
    alterations_events: '',
    reflection_notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isGeneratingGC, setIsGeneratingGC] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        date: event.date || '',
        start_time: event.start_time || '',
        end_time: event.end_time || '',
        description: event.description || '',
        lesson_plan: event.lesson_plan || '',
        subject: event.subject || '',
        room: event.room || '',
        alterations_events: event.alterations_events || '',
        reflection_notes: event.reflection_notes || ''
      });
    } else {
      // Set default date to today
      const today = new Date();
      setFormData(prev => ({
        ...prev,
        date: format(today, 'yyyy-MM-dd')
      }));
    }
  }, [event]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const generateGCText = async () => {
    if (!formData.subject.trim()) {
      alert('Please enter a subject first');
      return;
    }

    setIsGeneratingGC(true);
    
    try {
      const response = await fetch('/api/generate-gc-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ topic: formData.subject }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate GC text');
      }

      const data = await response.json();
      
      // Update the description field with the generated text
      setFormData(prev => ({
        ...prev,
        description: data.text
      }));
      
    } catch (error) {
      console.error('Error generating GC text:', error);
      alert('Failed to generate GC text. Please try again.');
    } finally {
      setIsGeneratingGC(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    if (formData.start_time && formData.end_time) {
      if (formData.start_time >= formData.end_time) {
        newErrors.end_time = 'End time must be after start time';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      onDelete(event.id);
    }
  };

  const handleCopyToOtherYear8Group = async () => {
    if (!event || !event.title) return;
    
    // Check if this is a Year 8 lesson
    const title = event.title.toLowerCase();
    if (!title.includes('year 8') || (!title.includes('8w/ma1') && !title.includes('8e/ma1'))) {
      alert('This feature is only available for Year 8 lessons.');
      return;
    }

    // Determine the other Year 8 group
    const currentGroup = title.includes('8w/ma1') ? '8W/Ma1' : '8E/Ma1';
    const otherGroup = currentGroup === '8W/Ma1' ? '8E/Ma1' : '8W/Ma1';
    
    try {
      const response = await fetch(`/api/events/copy-to-other-year8/${event.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          otherGroup: otherGroup,
          lessonPlan: formData.lesson_plan,
          description: formData.description,
          alterationsEvents: formData.alterations_events
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Lesson content copied to next available ${otherGroup} lesson on ${result.nextLessonDate}`);
        onClose(); // Close the modal to refresh the view
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error copying lesson:', error);
      alert('Failed to copy lesson content. Please try again.');
    }
  };

  const isEditing = !!event;
  const isYear8Lesson = event && event.title && 
    event.title.toLowerCase().includes('year 8') && 
    (event.title.toLowerCase().includes('8w/ma1') || event.title.toLowerCase().includes('8e/ma1'));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isEditing ? 'Edit Event' : 'New Event'}
          </h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">
              <BookOpen size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter event title"
              required
            />
            {errors.title && <div style={{ color: '#FF3B30', fontSize: '0.9rem', marginTop: '0.25rem' }}>{errors.title}</div>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <Calendar size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="form-input"
                required
              />
              {errors.date && <div style={{ color: '#FF3B30', fontSize: '0.9rem', marginTop: '0.25rem' }}>{errors.date}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <MapPin size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Room
              </label>
              <input
                type="text"
                name="room"
                value={formData.room}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., Room 101"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <Clock size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Start Time
              </label>
              <input
                type="time"
                name="start_time"
                value={formData.start_time}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Clock size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                End Time
              </label>
              <input
                type="time"
                name="end_time"
                value={formData.end_time}
                onChange={handleInputChange}
                className="form-input"
              />
              {errors.end_time && <div style={{ color: '#FF3B30', fontSize: '0.9rem', marginTop: '0.25rem' }}>{errors.end_time}</div>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label-with-button">
              <span>
                <User size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Subject
              </span>
              <button
                type="button"
                className="generate-gc-text-btn"
                onClick={generateGCText}
                disabled={isGeneratingGC || !formData.subject.trim()}
                title="Generate Google Classroom text for this subject"
              >
            {isGeneratingGC ? (
              <>
                <div className="loading-spinner-small"></div>
                Generating...
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Generate Objectives
              </>
            )}
              </button>
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g., Mathematics, English, Science"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Brief description of the event"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Lesson Plan</label>
            <textarea
              name="lesson_plan"
              value={formData.lesson_plan}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Detailed lesson plan, objectives, activities, resources needed, etc."
              rows={6}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Alterations and Events</label>
            <textarea
              name="alterations_events"
              value={formData.alterations_events}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Record any changes to the lesson, unexpected events, or important notes about what happened during the lesson."
              rows={4}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Reflection Notes</label>
            <textarea
              name="reflection_notes"
              value={formData.reflection_notes}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Reflect on how the lesson went, what worked well, what could be improved, and any insights for future lessons. These notes will be carried forward to the next lesson of the same class."
              rows={4}
            />
          </div>

          <div className="button-group">
            {isEditing && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
              >
                <Trash2 size={16} />
                Delete
              </button>
            )}
            
            {isEditing && isYear8Lesson && (
              <button
                type="button"
                className="btn btn-info"
                onClick={handleCopyToOtherYear8Group}
                title="Copy lesson content to next available lesson for the other Year 8 group"
              >
                <Copy size={16} />
                Copy to Other Year 8 Group
              </button>
            )}
            
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="btn btn-primary"
            >
              <Save size={16} />
              {isEditing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;


